import { Button, DialogActions, DialogContent, DialogTitle } from "@material-ui/core";
import { PaymentElement } from "@stripe/react-stripe-js";
import { PaymentMethod, Stripe, StripeElements } from "@stripe/stripe-js";
import React, { forwardRef, useEffect, useState } from "react";
import styled from "styled-components";

import { PaymentRequiredError } from "apis-v2/errors";
import BillingController, { BillingControllerState } from "js/controllers/BillingController";
import getLogger, { LogGroup } from "js/core/logger";
import { StripeCardError } from "js/core/services/stripe";
import ux from "js/core/services/userExperiences";
import { BeautifulDialog } from "js/react/components/Dialogs/BaseDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";

import withElements from "../utils/withElements";
import withStripe from "../utils/withStripe";
import { t } from "js/i18n";

const logger = getLogger(LogGroup.BILLING);

interface ChangePaymentMethodDialogProps {
    closeDialog: (success: boolean | PaymentMethod | { canceled: boolean }) => void;
    elements: StripeElements;
    stripe: Stripe;
    /**
     * Set to false when this dialog is opened on top of another tracked user experience (e.g. inviting users),
     * registering a nested experience would interrupt the parent one
     */
    trackExperience?: boolean;
}

const noopUx: typeof ux.updatePaymentMethod = {
    register: () => { },
    update: () => { },
    complete: () => { },
    fail: () => { },
    abandon: () => { }
};

const PaymentError = styled.div`
    color: white;
    background: orangered;
    letter-spacing: 0px;
    padding: 8px 15px;
    margin-top: 20px;
    font-size: 15px;
    font-weight: 600;
`;

const ChangePaymentMethodDialog =
    BillingController.withInitializedState(
        withStripe(
            withElements(
                forwardRef(
                    function ChangePaymentMethodDialog(props: ChangePaymentMethodDialogProps & BillingControllerState, ref: React.RefObject<BeautifulDialog>) {
                        const { elements, closeDialog, stripe, stripeData: { subscription }, trackExperience = true } = props;
                        const [isSubmitting, setIsSubmitting] = useState(false);
                        const [error, setError] = useState(null);

                        const updatePaymentMethodUx = trackExperience ? ux.updatePaymentMethod : noopUx;

                        useEffect(() => {
                            updatePaymentMethodUx.register({ subscriptionStatus: subscription?.status });
                        }, []);

                        const handleUpdatePaymentMethod = async () => {
                            setIsSubmitting(true);
                            setError(null);

                            const { error: submitError } = await elements.submit();
                            if (submitError) {
                                logger.error(new Error(submitError.message), "elements.submit() failed");
                                // Form validation error, the user stays in the dialog and can retry
                                updatePaymentMethodUx.update({ paymentError: { type: submitError.type, code: submitError.code } });
                                setError(submitError.message);
                                setIsSubmitting(false);
                                return;
                            }

                            const { paymentMethod, error } = await stripe.createPaymentMethod({ elements });
                            if (error) {
                                logger.error(new Error(error.message), "stripe.createPaymentMethod() failed");
                                // Card error (declined, etc.), the user stays in the dialog and can retry
                                updatePaymentMethodUx.update({ paymentError: { type: error.type, code: error.code, declineCode: error.decline_code } });
                                setIsSubmitting(false);
                                setError(error.message);
                                return;
                            }

                            try {
                                // If there is no subscription, we have nothing to update so we return the new payment method to be handled by the caller
                                if (!BillingController.subscription) {
                                    setIsSubmitting(false);
                                    updatePaymentMethodUx.complete();
                                    closeDialog(paymentMethod);
                                    return;
                                }

                                await BillingController.updatePaymentMethod(paymentMethod.id);

                                const shouldPayLatestInvoice = subscription.status === "past_due" || (subscription.status === "active" && !!subscription.pending_update);
                                if (!shouldPayLatestInvoice) {
                                    updatePaymentMethodUx.complete();
                                    closeDialog(paymentMethod);
                                    return;
                                }

                                await BillingController.payLatestInvoiceOnSubscription();

                                updatePaymentMethodUx.complete();
                                closeDialog(paymentMethod);
                            } catch (err) {
                                logger.error(err, "Api.paymentMethods.post() failed", { paymentMethodId: paymentMethod?.id });
                                if (err instanceof PaymentRequiredError) {
                                    // Server-side payment error (card declined, etc.), the user stays in the dialog and can retry
                                    // The response body carries analytics data shaped by StripeUtils.StripeError.AnalyticsResponseBody
                                    updatePaymentMethodUx.update({
                                        paymentError: {
                                            name: err.name,
                                            message: err.message,
                                            type: err.responseBody?.error_type,
                                            declineCode: err.responseBody?.decline_code,
                                            fundingType: err.responseBody?.funding_type,
                                            paymentType: err.responseBody?.payment_type
                                        }
                                    });
                                } else if (err instanceof StripeCardError) {
                                    // Client-side card error (e.g. declined during 3DS confirmation), the user stays in the dialog and can retry
                                    updatePaymentMethodUx.update({
                                        paymentError: {
                                            name: err.name,
                                            message: err.message,
                                            type: err.type,
                                            code: err.code,
                                            declineCode: err.declineCode
                                        }
                                    });
                                } else {
                                    // The user stays in the dialog and can retry, so register a fresh experience after failing
                                    updatePaymentMethodUx.fail({ error: err }, undefined, true);
                                }
                                setError(err.message);
                            }

                            setIsSubmitting(false);
                        };

                        const handleCloseDialog = () => {
                            updatePaymentMethodUx.abandon();
                            closeDialog(false);
                        };

                        const handleCancelDialog = () => {
                            updatePaymentMethodUx.abandon();
                            closeDialog({ canceled: true });
                        };

                        return (
                            <BeautifulDialog preventClose closeDialog={handleCloseDialog} ref={ref}>
                                <FetchingClickShield visible={isSubmitting} backgroundColor="white" />
                                <DialogTitle>{t("Update Payment Method")}</DialogTitle>
                                <DialogContent>
                                    <PaymentElement
                                        options={
                                            {
                                                layout: {
                                                    type: "tabs",
                                                    defaultCollapsed: false
                                                },
                                                wallets: {
                                                    link: "auto",
                                                }
                                            }
                                        }
                                    />
                                    {error && <PaymentError>{error}</PaymentError>}
                                </DialogContent>
                                <DialogActions>
                                    <Button color="secondary" disabled={isSubmitting} onClick={handleCancelDialog}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button color="primary" disabled={isSubmitting} onClick={handleUpdatePaymentMethod} data-test-id="update-payment-method-button">
                                        {t("Update Payment Method")}
                                    </Button>
                                </DialogActions>
                            </BeautifulDialog >
                        );
                    })
            )
        )
    );

export default ChangePaymentMethodDialog;
